/**
 * Presence Indicator Component
 *
 * Displays user online status with a colored badge.
 * Integrates with CCMS SDK's PresenceHub for real-time status updates.
 *
 * Status Types:
 * - Online (green) - User is active
 * - Away (yellow) - User is idle (5+ minutes)
 * - Busy (red) - User set status to busy
 * - Offline (gray) - User is not connected
 *
 * Usage:
 * ```tsx
 * <PresenceIndicator userId="123" size="sm" showLabel />
 * ```
 */

import { useEffect, useState } from 'react';
import { Badge, Tooltip, Group } from '@mantine/core';
import { IconCircle } from '@tabler/icons-react';
import { usePresence } from '@actschurch/ccms-sdk';

// Note: SDK exports PresenceStatus enum with capitalized values: 'Online', 'Away', 'Busy', 'Offline'
// We keep local lowercase type for UI consistency
export type PresenceStatus = 'online' | 'away' | 'busy' | 'offline';

export interface PresenceIndicatorProps {
  /** User ID to check presence for */
  userId: string;
  /** Size of the indicator */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** Show status label text */
  showLabel?: boolean;
  /** Custom label override */
  label?: string;
  /** Show tooltip with detailed status */
  showTooltip?: boolean;
}

/**
 * Map presence status to badge color
 */
const getStatusColor = (status: PresenceStatus): string => {
  switch (status) {
    case 'online':
      return 'green';
    case 'away':
      return 'yellow';
    case 'busy':
      return 'red';
    case 'offline':
    default:
      return 'gray';
  }
};

/**
 * Map presence status to label text
 */
const getStatusLabel = (status: PresenceStatus): string => {
  switch (status) {
    case 'online':
      return 'Online';
    case 'away':
      return 'Away';
    case 'busy':
      return 'Busy';
    case 'offline':
    default:
      return 'Offline';
  }
};

export function PresenceIndicator({
  userId,
  size = 'sm',
  showLabel = false,
  label,
  showTooltip = true,
}: PresenceIndicatorProps) {
  const { state, getUserPresence } = usePresence();
  const [status, setStatus] = useState<PresenceStatus>('offline');

  // Get initial presence on mount
  useEffect(() => {
    const fetchPresence = async () => {
      try {
        const presence = await getUserPresence(userId);
        if (presence) {
          // SDK returns capitalized status: 'Online', 'Away', 'Busy', 'Offline'
          setStatus(presence.status.toLowerCase() as PresenceStatus);
        } else {
          setStatus('offline');
        }
      } catch (error) {
        console.error('[PresenceIndicator] Failed to get user presence:', error);
        setStatus('offline');
      }
    };

    fetchPresence();
  }, [userId, getUserPresence]);

  // Real-time updates via state.onlineUsers Map (updates automatically via WebSocket)
  useEffect(() => {
    const userPresence = state.onlineUsers.get(userId);
    if (userPresence) {
      // User is in online users map, use their status
      setStatus(userPresence.status.toLowerCase() as PresenceStatus);
    } else if (state.isConnected) {
      // User not in map and we're connected = user is offline
      setStatus('offline');
    }
    // If not connected, keep current status (don't incorrectly mark as offline)
  }, [state.onlineUsers, state.isConnected, userId]);

  const statusColor = getStatusColor(status);
  const statusLabel = label || getStatusLabel(status);

  const indicator = showLabel ? (
    <Badge
      size={size}
      color={statusColor}
      variant="dot"
      leftSection={<IconCircle size={8} fill="currentColor" />}
    >
      {statusLabel}
    </Badge>
  ) : (
    <Group gap={4}>
      <IconCircle
        size={size === 'xs' ? 8 : size === 'sm' ? 10 : size === 'md' ? 12 : 14}
        fill={`var(--mantine-color-${statusColor}-6)`}
        stroke="none"
      />
    </Group>
  );

  if (showTooltip && !showLabel) {
    return <Tooltip label={statusLabel}>{indicator}</Tooltip>;
  }

  return indicator;
}

/**
 * Simple presence dot (no label, no tooltip)
 */
export function PresenceDot({
  userId,
  size = 10,
}: {
  userId: string;
  size?: number;
}) {
  const { state, getUserPresence } = usePresence();
  const [status, setStatus] = useState<PresenceStatus>('offline');

  // Get initial presence
  useEffect(() => {
    const fetchPresence = async () => {
      try {
        const presence = await getUserPresence(userId);
        if (presence) {
          setStatus(presence.status.toLowerCase() as PresenceStatus);
        } else {
          setStatus('offline');
        }
      } catch (error) {
        setStatus('offline');
      }
    };

    fetchPresence();
  }, [userId, getUserPresence]);

  // Real-time updates via state.onlineUsers
  useEffect(() => {
    const userPresence = state.onlineUsers.get(userId);
    if (userPresence) {
      setStatus(userPresence.status.toLowerCase() as PresenceStatus);
    } else if (state.isConnected) {
      setStatus('offline');
    }
  }, [state.onlineUsers, state.isConnected, userId]);

  return (
    <IconCircle
      size={size}
      fill={`var(--mantine-color-${getStatusColor(status)}-6)`}
      stroke="none"
      style={{ flexShrink: 0 }}
    />
  );
}
